import { createSlice } from '@reduxjs/toolkit';
import {
  requestCmsUpdate,
  requestFormats,
  requestPricing,
  requestTableData,
  requestUpdatePriceQuantity,
} from 'services/api/scoping';

const initialState = {
  pricing: {
    data: null,
    fetching: false,
  },
  formats: {
    data: null,
    fetching: false,
    error: null,
  },
  tableData: {
    data: null,
    fetching: false,
    error: null,
  },
  cmsUpdate: {
    fetching: false,
    error: null,
  },
  priceQuantityUpdate: {
    fetching: false,
    error: null,
  },
  error: null,
};

const scoping = createSlice({
  name: 'scoping',
  initialState,
  reducers: {
    initScopingPricing: (state) => {
      state.pricing.fetching = true;
      state.error = null;
    },
    successScopingPricing: (state, { payload }) => {
      state.pricing.data = payload;
      state.pricing.fetching = false;
      state.error = null;
    },
    errorScopingPricing: (state, { payload }) => {
      state.pricing.fetching = false;
      state.error = payload;
    },
    initScopingFormats: (state) => {
      state.formats.fetching = true;
      state.formats.error = null;
    },
    successScopingFormats: (state, { payload }) => {
      state.formats.data = payload;
      state.formats.fetching = false;
      state.formats.error = null;
    },
    errorScopingFormats: (state, { payload }) => {
      state.formats.fetching = false;
      state.formats.error = payload;
    },
    initScopingTableData: (state) => {
      state.tableData.fetching = true;
      state.tableData.error = null;
    },
    successScopingTableData: (state, { payload }) => {
      state.tableData.data = payload;
      state.tableData.fetching = false;
      state.tableData.error = null;
    },
    errorScopingTableData: (state, { payload }) => {
      state.tableData.fetching = false;
      state.tableData.error = payload;
    },
    initScopingCmsUpdate: (state) => {
      state.cmsUpdate.fetching = true;
      state.cmsUpdate.error = null;
    },
    successScopingCmsUpdate: (state) => {
      state.cmsUpdate.fetching = false;
      state.cmsUpdate.error = null;
    },
    errorScopingCmsUpdate: (state, { payload }) => {
      state.cmsUpdate.fetching = false;
      state.cmsUpdate.error = payload;
    },
    initScopingPriceQuantityUpdate: (state) => {
      state.priceQuantityUpdate.fetching = true;
      state.priceQuantityUpdate.error = null;
    },
    successScopingPriceQuantityUpdate: (state) => {
      state.priceQuantityUpdate.fetching = false;
      state.priceQuantityUpdate.error = null;
    },
    errorScopingPriceQuantityUpdate: (state, { payload }) => {
      state.priceQuantityUpdate.fetching = false;
      state.priceQuantityUpdate.error = payload;
    },
    resetScopingPricing: (state) => {
      state.pricing = { ...initialState.pricing };
      state.formats = { ...initialState.formats };
      state.tableData = { ...initialState.tableData };
      state.cmsUpdate = { ...initialState.cmsUpdate };
      state.priceQuantityUpdate = { ...initialState.priceQuantityUpdate };
      state.error = null;
    },
  },
});

export const {
  initScopingPricing,
  successScopingPricing,
  errorScopingPricing,
  initScopingFormats,
  successScopingFormats,
  errorScopingFormats,
  initScopingTableData,
  successScopingTableData,
  errorScopingTableData,
  initScopingCmsUpdate,
  successScopingCmsUpdate,
  errorScopingCmsUpdate,
  initScopingPriceQuantityUpdate,
  successScopingPriceQuantityUpdate,
  errorScopingPriceQuantityUpdate,
  resetScopingPricing,
} = scoping.actions;

export const getScopingPricing = (briefId) => async (dispatch) => {
  dispatch(initScopingPricing());

  const response = await requestPricing(briefId);

  if (response && response.success === false) {
    dispatch(errorScopingPricing(response.message));

    return {
      success: false,
      message: response.message,
      response,
    };
  }

  dispatch(successScopingPricing(response));

  return {
    success: true,
    data: response,
    response,
  };
};

export const getScopingFormats = (briefId) => async (dispatch) => {
  dispatch(initScopingFormats());

  let response;

  try {
    response = await requestFormats(briefId);
  } catch (error) {
    const message =
      (error && error.message) || 'Unable to load scoping formats';

    dispatch(errorScopingFormats(message));

    return {
      success: false,
      message,
      error,
    };
  }

  if (response && response.success === false) {
    dispatch(errorScopingFormats(response.message));

    return {
      success: false,
      message: response.message,
      response,
    };
  }

  dispatch(successScopingFormats(response));

  return {
    success: true,
    data: response,
    response,
  };
};

export const getScopingTableData = (briefId) => async (dispatch) => {
  dispatch(initScopingTableData());

  let response;

  try {
    response = await requestTableData(briefId);
  } catch (error) {
    const message =
      (error && error.message) || 'Unable to load scoping table data';

    dispatch(errorScopingTableData(message));

    return {
      success: false,
      message,
      error,
    };
  }

  if (response && response.success === false) {
    dispatch(errorScopingTableData(response.message));

    return {
      success: false,
      message: response.message,
      response,
    };
  }

  dispatch(successScopingTableData(response));

  return {
    success: true,
    data: response,
    response,
  };
};

// `id` is the brief id when subject is 'cms' (editing section_one...six), or
// the table_data row's own id when subject is 'table_date' (editing a
// service row's service_type/service_description/price).
export const updateScopingCms = ({ id, key, value, subject }) => async (
  dispatch
) => {
  dispatch(initScopingCmsUpdate());

  let response;

  try {
    response = await requestCmsUpdate({ id, key, value, subject });
  } catch (error) {
    const message =
      (error && error.message) || 'Unable to update scoping table data';

    dispatch(errorScopingCmsUpdate(message));

    return {
      success: false,
      message,
      error,
    };
  }

  if (response && response.success === false) {
    dispatch(errorScopingCmsUpdate(response.message));

    return {
      success: false,
      message: response.message,
      response,
    };
  }

  dispatch(successScopingCmsUpdate());

  return {
    success: true,
    data: response,
    response,
  };
};

export const updateScopingPriceQuantity = ({
  briefId,
  packageId,
  version,
  key,
  value,
}) => async (dispatch) => {
  dispatch(initScopingPriceQuantityUpdate());

  let response;

  try {
    response = await requestUpdatePriceQuantity({
      briefId,
      packageId,
      version,
      key,
      value,
    });
  } catch (error) {
    const message =
      (error && error.message) || 'Unable to update scoping price quantity';

    dispatch(errorScopingPriceQuantityUpdate(message));

    return {
      success: false,
      message,
      error,
    };
  }

  if (response && response.success === false) {
    dispatch(errorScopingPriceQuantityUpdate(response.message));

    return {
      success: false,
      message: response.message,
      response,
    };
  }

  dispatch(successScopingPriceQuantityUpdate());

  return {
    success: true,
    data: response,
    response,
  };
};

export default scoping.reducer;
